import { Container, Graphics, Text } from 'pixi.js';
import type { Game } from '../core/game';
import { SettingsMenu, type GameSettings } from './settingsMenu';
import {
  COLORS,
  textStyle,
  drawPanel,
  drawBeveledButton,
  BUTTON_THEMES,
  type BeveledButtonColors,
} from './theme';

export class PauseMenu {
  container: Container;
  settingsMenu: SettingsMenu | null = null;
  private background: Graphics;
  private menuBackground: Graphics;
  private titleText: Text;
  private game: Game;
  private visible: boolean = false;
  private fileInput: HTMLInputElement;
  private onQuitToLobby?: () => void;

  constructor(
    game: Game,
    width: number,
    height: number,
    initialSettings?: GameSettings,
    onQuitToLobby?: () => void,
  ) {
    this.game = game;
    this.onQuitToLobby = onQuitToLobby;
    this.container = new Container();
    this.container.visible = false;

    const defaultSettings: GameSettings = initialSettings || {
      masterVolume: 0.5,
      voiceVolume: 0.5,
      soundEnabled: true,
      showDamageNumbers: true,
      cameraShake: true,
    };

    this.settingsMenu = new SettingsMenu(width, height, defaultSettings, game, () => {
      this.settingsMenu?.hide();
      this.container.visible = true;
    });

    // Semi-transparent dark overlay (slight blue tint to match the chrome)
    this.background = new Graphics();
    this.background.rect(0, 0, width, height);
    this.background.fill({ color: 0x06101f, alpha: 0.72 });
    this.container.addChild(this.background);

    // Menu background panel (beveled chrome)
    this.menuBackground = new Graphics();
    drawPanel(this.menuBackground, -200, -250, 400, 500, { radius: 18, accent: COLORS.gold });
    this.menuBackground.position.set(width / 2, height / 2);
    this.container.addChild(this.menuBackground);

    // Title (chunky gold display)
    this.titleText = new Text({
      text: 'PAUSED',
      style: textStyle({ size: 40, display: true, color: COLORS.gold, outline: 4 }),
    });
    this.titleText.anchor.set(0.5, 0.5);
    this.titleText.position.set(width / 2, height / 2 - 180);
    this.container.addChild(this.titleText);

    // Create buttons (clear hierarchy: resume green, quit red, dev tools slate)
    const buttonY = height / 2 - 100;
    this.createButton('Resume', width / 2, buttonY, () => this.hide(), BUTTON_THEMES.green);
    this.createButton(
      'Export State',
      width / 2,
      buttonY + 70,
      () => this.exportState(),
      BUTTON_THEMES.slate,
    );
    this.createButton(
      'Load State',
      width / 2,
      buttonY + 140,
      () => this.loadState(),
      BUTTON_THEMES.slate,
    );
    this.createButton(
      'Settings',
      width / 2,
      buttonY + 210,
      () => this.showSettings(),
      BUTTON_THEMES.blue,
    );
    this.createButton(
      'Quit to Lobby',
      width / 2,
      buttonY + 280,
      () => this.quitToLobby(),
      BUTTON_THEMES.danger,
    );

    this.fileInput = this.createFileInput();
  }

  private createButton(
    label: string,
    x: number,
    y: number,
    onClick?: () => void,
    theme: BeveledButtonColors = BUTTON_THEMES.blue,
    disabled: boolean = false,
  ): void {
    const button = new Graphics();
    drawBeveledButton(button, -150, -22, 300, 44, theme, 12);
    button.position.set(x, y);

    if (!disabled && onClick) {
      button.eventMode = 'static';
      button.cursor = 'pointer';
      button.on('pointerdown', onClick);
      button.on('pointerover', () => {
        button.tint = 0xdddddd;
      });
      button.on('pointerout', () => {
        button.tint = 0xffffff;
      });
    } else {
      button.alpha = 0.5;
    }

    const isGold = theme === BUTTON_THEMES.gold;
    const text = new Text({
      text: label,
      style: textStyle({ size: 20, weight: '700', color: isGold ? COLORS.ink : COLORS.text }),
    });
    text.anchor.set(0.5, 0.5);
    text.position.set(x, y);

    this.container.addChild(button);
    this.container.addChild(text);
  }

  private createFileInput(): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.style.display = 'none';
    document.body.appendChild(input);

    input.addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const jsonString = e.target?.result as string;
            this.game.loadState(jsonString);
            this.hide();
            console.log('[PAUSE MENU] Game state loaded successfully');
          } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Unknown error';
            alert(`Failed to load game state:\n${errorMsg}`);
            console.error('[PAUSE MENU] Load failed:', err);
          }
        };
        reader.readAsText(file);
      }
      // Reset input so same file can be loaded again
      input.value = '';
    });

    return input;
  }

  private exportState(): void {
    try {
      this.game.exportGameState();
      console.log('[PAUSE MENU] Game state exported');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      alert(`Failed to export game state:\n${errorMsg}`);
      console.error('[PAUSE MENU] Export failed:', err);
    }
  }

  private loadState(): void {
    this.fileInput.click();
  }

  show(): void {
    this.visible = true;
    this.container.visible = true;
  }

  hide(): void {
    this.visible = false;
    this.container.visible = false;
  }

  isVisible(): boolean {
    return this.visible || (this.settingsMenu?.isVisible() ?? false);
  }

  toggle(): void {
    if (this.settingsMenu?.isVisible()) {
      this.settingsMenu.hide();
      this.container.visible = true;
      return;
    }

    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  private showSettings(): void {
    this.container.visible = false;
    this.settingsMenu?.show();
  }

  private quitToLobby(): void {
    if (confirm('Are you sure you want to quit to lobby? All progress will be lost.')) {
      this.hide();
      if (this.onQuitToLobby) {
        this.onQuitToLobby();
      }
    }
  }

  getSettings(): GameSettings | null {
    return this.settingsMenu?.getSettings() || null;
  }

  resize(width: number, height: number): void {
    this.background.clear();
    this.background.rect(0, 0, width, height);
    this.background.fill({ color: 0x06101f, alpha: 0.72 });

    this.menuBackground.position.set(width / 2, height / 2);
    this.titleText.position.set(width / 2, height / 2 - 180);

    const buttonY = height / 2 - 100;
    const buttons = this.container.children.filter(
      (child) =>
        child instanceof Graphics && child !== this.background && child !== this.menuBackground,
    );
    const texts = this.container.children.filter(
      (child) => child instanceof Text && child !== this.titleText,
    );

    buttons.forEach((button, i) => {
      button.position.set(width / 2, buttonY + i * 70);
    });

    texts.forEach((text, i) => {
      text.position.set(width / 2, buttonY + i * 70);
    });

    this.settingsMenu?.resize(width, height);
  }

  destroy(): void {
    if (this.fileInput && this.fileInput.parentNode) {
      this.fileInput.parentNode.removeChild(this.fileInput);
    }
    this.settingsMenu?.destroy();
    this.container.destroy({ children: true });
  }
}
